import React, { useMemo, useState } from "react";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, Flame, Gift, Loader2, Mail, Minus, Phone, Plus } from "lucide-react";
import { api } from "../lib/api";
import { BUSINESS } from "../lib/assets";
import { HolidayImages, useHoliday } from "../components/Holiday";
import usePageMeta from "../hooks/usePageMeta";
import { PayPalCardNote } from "../components/PayPalCardNote";

const priceNum = (p) => { const m = String(p || "").replace(/,/g, "").match(/\d+(?:\.\d+)?/); return m ? Number(m[0]) : 0; };
const money = (n) => `$${n.toFixed(2)}`;
const inputCls = "w-full border-2 border-ink bg-white px-4 py-3 font-serif text-base focus:outline-none focus:ring-2 focus:ring-ink";

function CallUsButton({ testid, light }) {
  return (
    <a
      href={`tel:${BUSINESS.phoneRaw}`}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 border-2 px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] transition-colors ${light ? "border-bone text-bone hover:bg-bone hover:text-ink" : "border-ink bg-ink text-bone hover:bg-bone hover:text-ink"}`}
    >
      <Phone size={16} /> Call {BUSINESS.phone}
    </a>
  );
}

function ItemPicker({ items, qty, setQty }) {
  return (
    <ul className="divide-y divide-ink/20 border-2 border-ink bg-white" data-testid="holiday-item-picker">
      {items.map((it, i) => {
        const q = qty[it.id] || 0;
        return (
          <li key={it.id} className={`flex flex-wrap items-center gap-3 p-4 transition-colors ${q ? "bg-bone" : ""}`} data-testid={`holiday-pick-${i}`}>
            <div className="min-w-[180px] flex-1">
              <div className="font-alfa text-lg uppercase leading-tight">{it.name}</div>
              {it.desc && <div className="font-serif text-sm text-ink/70">{it.desc}</div>}
            </div>
            <div className="font-mono text-base font-bold tabular-nums">{it.price}</div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setQty(it.id, q - 1)} disabled={!q} className="border-2 border-ink p-2 hover:bg-ink hover:text-bone disabled:opacity-30" data-testid={`holiday-dec-${i}`} aria-label="Decrease"><Minus size={14} /></button>
              <span className="w-6 text-center font-mono" data-testid={`holiday-qty-${i}`}>{q}</span>
              <button type="button" onClick={() => setQty(it.id, q + 1)} className="border-2 border-ink p-2 hover:bg-ink hover:text-bone" data-testid={`holiday-inc-${i}`} aria-label="Increase"><Plus size={14} /></button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ReheatNote() {
  return (
    <div className="flex items-center gap-2 border-2 border-dashed border-[#b3261e] bg-[#b3261e]/5 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#b3261e]" data-testid="holiday-reheat-note">
      <Flame size={14} className="shrink-0" /> Reheat instructions included with every order
    </div>
  );
}

function Summary({ lines, subtotal, tax, total, rate }) {
  return (
    <div className="border-2 border-ink bg-white p-5 shadow-hard" data-testid="holiday-summary">
      <div className="eyebrow mb-3 text-ink/60">Your order</div>
      {lines.length === 0 ? <p className="font-serif text-sm text-ink/60">Add items above to get started.</p> : (
        <ul className="space-y-1 font-serif text-sm">
          {lines.map((l) => <li key={l.id} className="flex justify-between gap-2"><span>{l.qty}× {l.name}</span><span className="font-mono">{money(l.price * l.qty)}</span></li>)}
        </ul>
      )}
      <dl className="mt-4 space-y-1 border-t border-ink pt-3 font-mono text-sm">
        <div className="flex justify-between"><dt className="text-ink/60">Subtotal</dt><dd data-testid="holiday-subtotal">{money(subtotal)}</dd></div>
        <div className="flex justify-between"><dt className="text-ink/60">Sales Tax ({Math.round(rate * 100)}%)</dt><dd data-testid="holiday-tax">{money(tax)}</dd></div>
        <div className="flex justify-between border-t border-ink pt-2 text-base font-bold"><dt>Total</dt><dd data-testid="holiday-total">{money(total)}</dd></div>
      </dl>
      <div className="mt-4"><ReheatNote /></div>
    </div>
  );
}

function PayStep({ order, onPaid, onBack }) {
  const [busy, setBusy] = useState(false);
  const createOrder = (_d, actions) => actions.order.create({
    intent: "CAPTURE",
    purchase_units: [{
      description: `When Pigs Fly BBQ — Holiday pickup ${order.pickup_date}`.slice(0, 127),
      custom_id: order.id,
      amount: {
        currency_code: "USD",
        value: order.total.toFixed(2),
        breakdown: {
          item_total: { currency_code: "USD", value: order.subtotal.toFixed(2) },
          tax_total: { currency_code: "USD", value: order.tax.toFixed(2) },
        },
      },
      items: order.items.map((l) => ({ name: l.name.slice(0, 127), quantity: String(l.qty), unit_amount: { currency_code: "USD", value: l.price.toFixed(2) }, category: "PHYSICAL_GOODS" })),
    }],
    application_context: { shipping_preference: "NO_SHIPPING", brand_name: "When Pigs Fly Family BBQ", user_action: "PAY_NOW" },
  });
  const onApprove = async (_d, actions) => {
    setBusy(true);
    try {
      const details = await actions.order.capture();
      const p = details?.payer;
      const { data } = await api.post(`/holiday-orders/${order.id}/paid`, {
        paypal_order_id: details?.id || _d?.orderID || "",
        payer_name: p?.name ? `${p.name.given_name || ""} ${p.name.surname || ""}`.trim() : "",
        payer_email: p?.email_address || "",
      });
      onPaid(data);
    } catch (e) {
      console.error(e);
      toast.error("Payment couldn't be completed. Please try again or call us.");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="holiday-pay-step">
      <div className="border-2 border-ink bg-white p-5 font-serif text-sm">
        <p><b>{order.name}</b> · {order.phone}{order.email ? ` · ${order.email}` : ""}</p>
        <p className="mt-1 flex items-center gap-2"><CalendarDays size={14} /> Pickup: <b>{order.pickup_date}</b></p>
      </div>
      <Summary lines={order.items.map((l) => ({ ...l, id: l.item_id }))} subtotal={order.subtotal} tax={order.tax} total={order.total} rate={0.07} />
      <div data-testid="holiday-paypal-button" className={busy ? "pointer-events-none opacity-60" : ""}>
        <PayPalButtons
          key={order.id}
          style={{ layout: "vertical", shape: "rect", color: "black", label: "checkout", height: 48 }}
          createOrder={createOrder}
          onApprove={onApprove}
          onCancel={() => toast("Payment cancelled — your order is saved, you can pay anytime.")}
          onError={(e) => { console.error(e); toast.error("PayPal ran into an issue. Try again or call us."); }}
        />
        <PayPalCardNote testid="holiday-card-note" />
      </div>
      <button type="button" onClick={onBack} className="font-mono text-[11px] uppercase tracking-[0.2em] underline" data-testid="holiday-edit-order">← Edit my order</button>
    </div>
  );
}

function DoneStep({ order }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="border-2 border-ink bg-white p-8 text-center shadow-hard" data-testid="holiday-success">
      <CheckCircle2 size={48} className="mx-auto text-[#b3261e]" />
      <h2 className="mt-4 font-rye text-3xl uppercase md:text-4xl">You're all set, {order.name.split(" ")[0]}!</h2>
      <p className="mx-auto mt-3 max-w-lg font-serif text-base text-ink/80">
        Your holiday order is paid ({money(order.total)}). Pickup on <b>{order.pickup_date}</b> at {BUSINESS.street}, {BUSINESS.city}. We'll call you to confirm the details.
      </p>
      <div className="mx-auto mt-4 max-w-md"><ReheatNote /></div>
      <div className="mt-6 border-t border-ink/20 pt-6">
        <p className="eyebrow mb-3 text-ink/60">Questions about your order?</p>
        <div className="flex flex-wrap justify-center gap-3">
          <CallUsButton testid="holiday-success-call" />
          <a href={`mailto:${BUSINESS.email}`} data-testid="holiday-success-email" className="inline-flex items-center gap-2 border-2 border-ink px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] hover:bg-ink hover:text-bone"><Mail size={16} /> Email us</a>
        </div>
      </div>
    </motion.div>
  );
}

function OrderForm({ h, onCreated, replaceId, hidden }) {
  const [qty, setQtyMap] = useState({});
  const [form, setForm] = useState({ name: "", phone: "", email: "", pickup_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const setQty = (id, q) => setQtyMap((m) => ({ ...m, [id]: Math.max(0, Math.min(50, q)) }));
  const lines = useMemo(() => h.items.filter((it) => qty[it.id] > 0).map((it) => ({ id: it.id, name: it.name, price: priceNum(it.price), qty: qty[it.id] })), [h.items, qty]);
  const subtotal = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const tax = Math.round(subtotal * h.tax_rate * 100) / 100;
  const submit = async (e) => {
    e.preventDefault();
    if (!lines.length) return toast.error("Pick at least one item");
    if (!form.pickup_date) return toast.error("Choose a pickup day");
    setBusy(true);
    try {
      const { data } = await api.post("/holiday-orders", { ...form, replace_id: replaceId || "", items: lines.map((l) => ({ item_id: l.id, qty: l.qty })) });
      onCreated(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check your name and phone number");
    } finally { setBusy(false); }
  };
  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  return (
    <form onSubmit={submit} className={`${hidden ? "hidden" : "grid"} grid-cols-1 gap-8 lg:grid-cols-12`} data-testid="holiday-order-form">
      <div className="space-y-8 lg:col-span-7">
        <div>
          <h2 className="mb-3 font-alfa text-2xl uppercase">1. Pick your items</h2>
          <ItemPicker items={h.items} qty={qty} setQty={setQty} />
        </div>
        <div>
          <h2 className="mb-3 font-alfa text-2xl uppercase">2. Pickup day</h2>
          <div className="flex flex-wrap gap-3" data-testid="holiday-pickup-options">
            {h.pickup_dates.map((d, i) => (
              <button type="button" key={d} onClick={() => setForm({ ...form, pickup_date: d })} data-testid={`holiday-pickup-${i}`}
                className={`inline-flex items-center gap-2 border-2 border-ink px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${form.pickup_date === d ? "bg-ink text-bone" : "bg-white hover:bg-ink/10"}`}>
                <CalendarDays size={14} /> {d}
              </button>
            ))}
          </div>
        </div>
        <div>
          <h2 className="mb-3 font-alfa text-2xl uppercase">3. Your info</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <input required placeholder="Full name *" className={inputCls} {...f("name")} data-testid="holiday-name" />
            <input required type="tel" placeholder="Phone number *" className={inputCls} {...f("phone")} data-testid="holiday-phone" />
            <input type="email" placeholder="Email (optional)" className={`${inputCls} md:col-span-2`} {...f("email")} data-testid="holiday-email" />
            <textarea rows={2} placeholder="Notes (optional) — e.g. sliced vs whole" className={`${inputCls} md:col-span-2`} {...f("notes")} data-testid="holiday-notes" />
          </div>
        </div>
      </div>
      <div className="lg:col-span-5">
        <div className="space-y-4 lg:sticky lg:top-28">
          <Summary lines={lines} subtotal={subtotal} tax={tax} total={subtotal + tax} rate={h.tax_rate} />
          <button type="submit" disabled={busy} data-testid="holiday-continue-btn"
            className="flex w-full items-center justify-center gap-2 border-2 border-ink bg-[#b3261e] px-6 py-4 font-alfa text-lg uppercase tracking-[0.16em] text-bone transition-transform hover:-translate-y-0.5 disabled:opacity-60">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Gift size={18} />} Continue to payment
          </button>
          <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">Secure checkout with PayPal or card</p>
        </div>
      </div>
    </form>
  );
}

export default function HolidayMenuPage() {
  usePageMeta({ title: "Holiday Menu — Order Ahead", description: "Order smoked Thanksgiving brisket, turkey, spiral ham and family-size sides from When Pigs Fly Family BBQ in Spring Hill, FL. Pay online, pick up hot.", path: "/holiday-menu" });
  const h = useHoliday();
  const [order, setOrder] = useState(null);
  const [paid, setPaid] = useState(null);
  const [lastId, setLastId] = useState("");
  const toTop = () => (window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0));
  const onCreated = (o) => { setOrder(o); setLastId(o.id); toTop(); };
  return (
    <div>
      <section className="relative overflow-hidden border-b border-ink bg-[#b3261e] text-bone" data-testid="holiday-hero">
        <div className="grain-block absolute inset-0 opacity-40" />
        <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-4 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-7">
            <div className="eyebrow mb-3 flex items-center gap-2 text-bone/80"><Gift size={14} /> {h?.eyebrow || "Limited time"}</div>
            <h1 className="font-rye text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-7xl">{h?.title || "Holiday Menu"}</h1>
            {h?.subtitle && <p className="mt-6 max-w-xl font-serif text-base leading-relaxed text-bone/90 md:text-lg">{h.subtitle}</p>}
            <div className="mt-8"><CallUsButton testid="holiday-hero-call" light /></div>
          </div>
          {h && <div className="md:col-span-5"><HolidayImages h={h} /></div>}
        </div>
      </section>
      <section className="bg-bone py-12 md:py-16">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          {!h ? (
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/60"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : !h.enabled ? (
            <div className="border-2 border-ink bg-white p-8 text-center" data-testid="holiday-closed">
              <h2 className="font-alfa text-2xl uppercase">Holiday ordering is closed</h2>
              <p className="mt-2 font-serif text-ink/70">Give us a call and we'll see what we can do.</p>
              <div className="mt-4"><CallUsButton testid="holiday-closed-call" /></div>
            </div>
          ) : paid ? <DoneStep order={paid} />
            : (
              <>
                {order && <div className="mx-auto max-w-xl"><PayStep order={order} onPaid={(o) => { setPaid(o); toTop(); }} onBack={() => setOrder(null)} /></div>}
                <OrderForm h={h} onCreated={onCreated} replaceId={lastId} hidden={!!order} />
              </>
            )}
        </div>
      </section>
    </div>
  );
}
